import React, { useEffect, useRef, useState } from 'react';
import FullScreenLoader from '../../../../components/loading/FullScreenLoader';
import { toast } from 'react-toastify';
import {
  downloadRelatorioCsvDreCategoria,
  downloadRelatorioXlsxDreCategoria,
} from '../../../../api/contac/contacReportsCategoriasApi';
import ArrowDown from '../../../../img/arrow-down.svg';
import ArrowUp from '../../../../img/arrow-up.svg';
import ContacDocumentOrange from '../../../../img/contac_document_orange.svg';
import './ModalCategoriaDre.css';
import '../placa-dre/ModalPlacaDre.css';

interface Detalhe {
  mes: string;
  valor: number;
}

interface Classificacao {
  classificacao: string;
  valor: number;
  detalhes: Detalhe[];
}

interface ClassificacaoTotal {
  classificacao: string;
  total: number;
  detalhes: Detalhe[];
}

interface Categoria {
  categoria: string;
  receitaBruta: number;            // todas as receitas
  receitaOperacional: number;      // receitas ligadas à placa
  outrasReceitas: number;          // rateio de entrada
  despesaBruta: number;            // todas as despesas
  despesaOperacional: number;      // despesas ligadas à placa
  resultadoOperacional: number;    // receita bruta - despesa operacional
  margemDeContribuicao: number;    // rateio de saída
  resultado: number;               // resultado operacional - margem de contribuição

  placasAtivas?: number;
  placasPeriodo?: number;

  receita?: number;
  saida?: number;
  receitaSga?: number;
  receitaComPlaca?: number;
  saidasEasy?: number;
  saidasNasajon?: number;
  resultadoLiquido?: number;
  resultadoBruto?: number;
  rateioSaida?: number;
  rateioEntrada?: number;

  entradasClassificacoesMensalidadeEComPlaca: Classificacao[];
  entradasClassificacoesRateio: Classificacao[];
  saidasClassificacoesDiretas: Classificacao[];
  saidasClassificacoesRateio: Classificacao[];
}

interface ModalCategoriaDreProps {
  isOpen: boolean;
  onClose: () => void;
  data: {
    periodo: string;

    resultadoTotal: { valor: number; porMes: Detalhe[] };
    entradasTotais: { valor: number; porMes: Detalhe[] };
    despesasTotais: { valor: number; porMes: Detalhe[] };

    classificacoesTotais?: ClassificacaoTotal[];
    classificacoesTotaisSaidas?: ClassificacaoTotal[];

    agregacoes?: {
      porCategoria?: (Categoria & {
        detalhamento: {
          receitaMensalidade: number;
          receitaRateio: number;
          receitaNasajonComPlaca: number;
          despesaNasajonComPlaca: number;
          despesaEasy: number;
          despesaRateio: number;
          despesaEasyExcedente: number;
          despesaRecorrenteExcedente?: number;
          despesaRecorrente?: number;
          resultado: number;
          classificacoesEntradaPlaca?: {
            classificacao: string;
            total: number;
            porMes: { competencia: string; valor: number }[];
          }[];
          classificacoesEntradaRateio?: {
            classificacao: string;
            total: number;
            porMes: { competencia: string; valor: number }[];
          }[];
          classificacoesSaidaPlaca?: {
            classificacao: string;
            total: number;
            porMes: { competencia: string; valor: number }[];
          }[];
          classificacoesSaidaRateio?: {
            classificacao: string;
            total: number;
            porMes: { competencia: string; valor: number }[];
          }[];
        };
      })[];
    };
  };
}

export type { ModalCategoriaDreProps };

const ModalCategoriaDre: React.FC<ModalCategoriaDreProps> = ({ isOpen, onClose, data }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<string[]>([]);

  const toggleExpand = (key: string) => {
    setExpanded((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const formatCurrency = (valor: number | null | undefined) =>
    typeof valor === 'number'
      ? valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })
      : '0,00';

  const formatPercent = (n: number | null | undefined) =>
    typeof n === 'number' && Number.isFinite(n)
      ? `${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
      : '—';

  const pctOf = (signedValue: number, baseAbs: number) =>
    baseAbs === 0 ? null : (signedValue / baseAbs) * 100;

  const renderValorPct = (
    displayValue: React.ReactNode,
    signedForPercent: number,
    baseAbs: number,
    percentClassName?: string
  ) => (
    <div className="dre-value-pct">
      {displayValue}
      <span className={`dre-percent ${percentClassName ?? ''}`}>
        {formatPercent(pctOf(signedForPercent, baseAbs))}
      </span>
    </div>
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleDownloadCsv = async () => {
    setLoading(true);
    try {
      const [dataInicio, dataFim] = String(data.periodo)
        .split(' até ')
        .map((s) => s.trim());

      await downloadRelatorioCsvDreCategoria({
        dataInicio,
        dataFim,
        categorias: (data.agregacoes?.porCategoria ?? [])
          .map((c) => String(c.categoria).trim())
          .filter(Boolean),
      });

      toast.success('Relatório CSV gerado com sucesso!');
    } catch {
      toast.error('Erro ao gerar CSV');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadXlsx = async () => {
    setLoading(true);
    try {
      const [dataInicio, dataFim] = String(data.periodo)
        .split(' até ')
        .map((s) => s.trim());

      await downloadRelatorioXlsxDreCategoria({
        dataInicio,
        dataFim,
        categorias: (data.agregacoes?.porCategoria ?? [])
          .map((c) => String(c.categoria).trim())
          .filter(Boolean),
      });

      toast.success('Relatório XLSX gerado com sucesso!');
    } catch {
      toast.error('Erro ao gerar XLSX');
    } finally {
      setLoading(false);
    }
  };
  if (!isOpen) return null;

  const classBySign3 = (n?: number) =>
    typeof n === 'number' && n < 0 ? 'value-negative3' : 'value-positive3';

  const mapClassResumo = (src?: {
    classificacao: string;
    total: number;
    porMes: { competencia: string; valor: number }[];
  }[]): Classificacao[] =>
    (src ?? []).map(x => ({
      classificacao: x.classificacao || 'Sem classificação',
      valor: Number(x.total) || 0,
      detalhes: (x.porMes ?? []).map(pm => ({
        mes: pm.competencia,
        valor: Number(pm.valor) || 0,
      })),
    }));

  return (
    <>
      {loading && <FullScreenLoader />}
      <div className="modal-filtro-overlay">
        <div className="status-modal-dre-categoria-content" ref={modalRef}>
          <div className="dre-title-set">
            <div className="modal-header">Demonstração do Resultado</div>
            <div className="dre-title-row">
              <div className="dre-title-spacer" />
              <div className="contac-perioodo">
                <div className="dre-title2">{data.periodo.replace(/(\d{4})-(\d{2})-(\d{2})/g, '$3-$2-$1')}</div>
              </div>
              <div className="dre-actions-container">
                <div className="dre-save-btn MouseHover" onClick={handleDownloadCsv}>
                  <div className="Frame1000003987">
                    <img
                      src={ContacDocumentOrange}
                      alt="Relatórios"
                      width="19.80"
                      height="19.80"
                      style={{ cursor: 'pointer' }}
                    />
                    <div className="ComponentName">Gerar CSV</div>
                  </div>
                </div>
                <div className="dre-save-btn MouseHover" onClick={handleDownloadXlsx}>
                  <div className="Frame1000003987">
                    <img
                      src={ContacDocumentOrange}
                      alt="Relatórios"
                      width="19.80"
                      height="19.80"
                      style={{ cursor: 'pointer' }}
                    />
                    <div className="ComponentName">Gerar XLSX</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-body-categoria">
            <div className="card financial">
              <div className="modal-header">Resumo Geral</div>

              <div className="dre-container">
                <div className="dre-title2">
                  Receita Total: 
                  <span className="value-positive">
                    R$ {formatCurrency(data.entradasTotais?.valor)}
                  </span>
                </div>

                <div className="dre-title2">
                  Saída Total: 
                  <span className="value-negative">
                    R$ -{formatCurrency(data.despesasTotais?.valor)}
                  </span>
                </div>

                <div className="dre-title2">
                  Resultado: 
                  <span className={
                    (data.resultadoTotal?.valor ?? 0) < 0
                      ? 'value-negative'
                      : (data.resultadoTotal?.valor ?? 0) > 0
                      ? 'value-positive'
                      : 'value-zero'
                  }>
                    R$ {formatCurrency(data.resultadoTotal?.valor)}
                  </span>
                </div>
              </div>
            </div>

            {Array.isArray(data.classificacoesTotais) && data.classificacoesTotais.length > 0 && (
              <div className="card">
                <div className="modal-header">Totais por Classificações de Entrada</div>
                <div className="subclass-container" style={{ borderLeft: 'none', paddingLeft: 0 }}>
                  {data.classificacoesTotais.map((item, idx) => (
                    <React.Fragment key={`ct-${idx}`}>
                      <div className="subclass-row clickable" onClick={() => toggleExpand(`ct-${idx}`)}>
                        <div className="subclass-label">
                          <span>{item.classificacao}</span>
                          <img
                            src={expanded.includes(`ct-${idx}`) ? ArrowUp : ArrowDown}
                            alt="Toggle"
                            className="imgIcon arrow-toggle"
                            style={{ marginLeft: 5 }}
                          />
                        </div>
                        <div className="subclass-value-pct">
                          <span className={`subclass-value ${item.total >= 0 ? 'value-positive4' : 'value-negative4'}`}>
                            R$ {formatCurrency(item.total)}
                          </span>
                        </div>
                      </div>
                      {expanded.includes(`ct-${idx}`) && (
                        <div className="subclass-container">
                          {(item.detalhes ?? []).map((det, dIdx) => (
                            <div className="detail-row" key={`ct-det-${idx}-${dIdx}`}>
                              <span className="detail-label">{det.mes}</span>
                              <div className="subclass-value-pct">
                                <span className={`detail-value ${det.valor >= 0 ? 'value-positive4' : 'value-negative4'}`}>
                                  R$ {formatCurrency(det.valor)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}

            {Array.isArray(data.classificacoesTotaisSaidas) && data.classificacoesTotaisSaidas.length > 0 && (
              <div className="card">
                <div className="modal-header">Totais por Classificações de Saída</div>
                <div className="subclass-container" style={{ borderLeft: 'none', paddingLeft: 0 }}>
                  {data.classificacoesTotaisSaidas.map((item, idx) => (
                    <React.Fragment key={`cts-${idx}`}>
                      <div className="subclass-row clickable" onClick={() => toggleExpand(`cts-${idx}`)}>
                        <div className="subclass-label">
                          <span>{item.classificacao}</span>
                          <img
                            src={expanded.includes(`cts-${idx}`) ? ArrowUp : ArrowDown}
                            alt="Toggle"
                            className="imgIcon arrow-toggle"
                            style={{ marginLeft: 5 }}
                          />
                        </div>
                        <div className="subclass-value-pct">
                          <span className={`subclass-value ${item.total >= 0 ? 'value-negative4' : 'value-positive4'}`}>
                            R$ {formatCurrency(item.total)}
                          </span>
                        </div>
                      </div>
                      {expanded.includes(`cts-${idx}`) && (
                        <div className="subclass-container">
                          {(item.detalhes ?? []).map((det, dIdx) => (
                            <div className="detail-row" key={`cts-det-${idx}-${dIdx}`}>
                              <span className="detail-label">{det.mes}</span>
                              <div className="subclass-value-pct">
                                <span className="detail-value value-negative4">
                                  R$ {formatCurrency(det.valor)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}

            <div className="categorias-grid">
              {(data.agregacoes?.porCategoria ?? []).map((cat, i) => {
                const receitaBruta = cat.receitaBruta ?? cat.receita ?? 0;
                const receitaOperacional = cat.receitaOperacional ?? ((cat.receitaSga ?? 0) + (cat.receitaComPlaca ?? 0));
                const outrasReceitas = cat.outrasReceitas ?? cat.detalhamento?.receitaRateio ?? cat.rateioEntrada ?? 0;
                const entradasOp = mapClassResumo(cat.detalhamento?.classificacoesEntradaPlaca);
                const entradasRate = mapClassResumo(cat.detalhamento?.classificacoesEntradaRateio);
                const saidasDirBase = mapClassResumo(cat.detalhamento?.classificacoesSaidaPlaca);

                const despesaRecorrenteExcedente = Number(cat.detalhamento?.despesaRecorrenteExcedente ?? 0) || 0;

                const alreadyHasExcedente = saidasDirBase.some((x) =>
                  String(x.classificacao).toLowerCase().includes('recorrente excedente')
                );

                const saidasDir = [
                  ...saidasDirBase,
                  ...(despesaRecorrenteExcedente && !alreadyHasExcedente
                    ? [{
                        classificacao: 'Despesa Recorrente Excedente',
                        valor: despesaRecorrenteExcedente,
                        detalhes: [{ mes: 'Período', valor: despesaRecorrenteExcedente }],
                      } as Classificacao]
                    : []),
                ];
                const saidasRate = mapClassResumo(cat.detalhamento?.classificacoesSaidaRateio);
                const despesasOperacionais = cat.despesaOperacional ?? (
                  (cat.saidasEasy ?? cat.detalhamento?.despesaEasy ?? 0) +
                  (cat.saidasNasajon ?? cat.detalhamento?.despesaNasajonComPlaca ?? 0) +
                  (cat.detalhamento?.despesaEasyExcedente ?? 0) +
                  (cat.detalhamento?.despesaRecorrenteExcedente ?? 0)
                );
                const margemContribuicao = cat.margemDeContribuicao ?? cat.detalhamento?.despesaRateio ?? cat.rateioSaida ?? 0;
                const resultadoOperacional = cat.resultadoOperacional ?? (receitaBruta - despesasOperacionais);
                const resultadoFinal = cat.resultado ?? (resultadoOperacional - margemContribuicao);
                const baseRbAbs = Math.abs(receitaBruta ?? 0);
                return (
                  <div className="modal-body categoria-body" key={i}>
                    <div className="card">
                      <div className="modal-header" style={{ marginBottom: 20 }}>
                        {cat.categoria}
                      </div>

                      <div className="row-between dre-columns-header" style={{ marginBottom: 12, borderBottom: '1px dashed var(--tema-linha, #eef2f5)', paddingBottom: 6 }}>
                        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                          <span className="dre-col-header" style={{ fontSize: '11px', display: 'flex', alignItems: 'center' }}>
                            Placas Ativas: <strong style={{ color: 'var(--tema-texto, #1A1C1B)', marginLeft: 4 }}>{Number(cat.placasAtivas ?? 0)}</strong>
                          </span>
                          <span className="dre-col-header" style={{ fontSize: '11px', display: 'flex', alignItems: 'center' }}>
                            Placas do Período: <strong style={{ color: 'var(--tema-texto, #1A1C1B)', marginLeft: 4 }}>{Number(cat.placasPeriodo ?? 0)}</strong>
                          </span>
                        </div>
                        <div className="dre-value-pct">
                          <span className="dre-col-header" style={{ minWidth: 120, textAlign: 'right' }}>Valor</span>
                          <span className="dre-col-header" style={{ minWidth: 70, textAlign: 'right' }}>% Rec. Bruta</span>
                        </div>
                      </div>
                      <div className="row-between">
                        <div className="dre-title">
                          Receita Bruta <span className="dre-note">(Todas as Receitas)</span>
                        </div>
                        {renderValorPct(
                          <span className="entry-text value-positive3">R$ {formatCurrency(receitaBruta)}</span>,
                          +Math.abs(receitaBruta),
                          baseRbAbs,
                          'value-positive5'
                        )}
                      </div>
                      <div
                        className="row-between clickable"
                        onClick={() => toggleExpand(`op-${i}`)}
                        aria-expanded={expanded.includes(`op-${i}`)}
                        title="Ver detalhes da Receita Operacional"
                      >
                        <div className="dre-title">
                          Receita Operacional <span className="dre-note">(Receitas ligadas às placas)</span>
                          <img
                            style={{ marginLeft: 5 }}
                            src={expanded.includes(`op-${i}`) ? ArrowUp : ArrowDown}
                            alt="Toggle"
                            className="imgIcon arrow-toggle"
                          />
                        </div>
                        {renderValorPct(
                          <span className="entry-text value-positive3">R$ {formatCurrency(receitaOperacional)}</span>,
                          +Math.abs(receitaOperacional),
                          baseRbAbs,
                          'value-positive5'
                        )}
                      </div>
                      {expanded.includes(`op-${i}`) && (
                        <div className="subclass-container">
                          {entradasOp.map((ent, idx) => (
                            <React.Fragment key={`ent-op-${i}-${idx}`}>
                              <div className="subclass-row">
                                <div className="subclass-label">
                                  <span>{ent.classificacao}</span>
                                  <img
                                    src={expanded.includes(`ent-op-${i}-${idx}`) ? ArrowUp : ArrowDown}
                                    alt="Toggle"
                                    className="imgIcon arrow-toggle clickable"
                                    onClick={() => toggleExpand(`ent-op-${i}-${idx}`)}
                                    style={{ marginLeft: 5 }}
                                  />
                                </div>
                                <div className="subclass-value-pct">
                                  <span className="subclass-value value-positive4">R$ {formatCurrency(ent.valor)}</span>
                                  <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                                </div>
                              </div>
                              {expanded.includes(`ent-op-${i}-${idx}`) && (
                                <div className="subclass-container">
                                  {ent.detalhes.map((det, dIdx) => (
                                    <div className="detail-row" key={`ent-op-det-${i}-${idx}-${dIdx}`}>
                                      <span className="detail-label">{det.mes}</span>
                                      <div className="subclass-value-pct">
                                        <span className="detail-value value-positive4">R$ {formatCurrency(det.valor)}</span>
                                        <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      )}

                      <div
                        className="row-between clickable"
                        onClick={() => toggleExpand(`oe-${i}`)}
                        aria-expanded={expanded.includes(`oe-${i}`)}
                        title="Ver detalhes de Outras Receitas"
                      >
                        <div className="dre-title">
                          Outras Receitas <span className="dre-note">(Receitas não ligadas às placas)</span>
                          <img
                            style={{ marginLeft: 5 }}
                            src={expanded.includes(`oe-${i}`) ? ArrowUp : ArrowDown}
                            alt="Toggle"
                            className="imgIcon arrow-toggle"
                          />
                        </div>
                        {renderValorPct(
                          <span className="entry-text value-positive3">R$ {formatCurrency(outrasReceitas)}</span>,
                          +Math.abs(outrasReceitas),
                          baseRbAbs,
                          'value-positive5'
                        )}
                      </div>
                      {expanded.includes(`oe-${i}`) && (
                        <div className="subclass-container">
                          {entradasRate.map((ent, idx) => (
                            <React.Fragment key={`ent-rateio-${i}-${idx}`}>
                              <div className="subclass-row">
                                <div className="subclass-label">
                                  <span>{ent.classificacao}</span>
                                  <img
                                    src={expanded.includes(`ent-rateio-${i}-${idx}`) ? ArrowUp : ArrowDown}
                                    alt="Toggle"
                                    className="imgIcon arrow-toggle clickable"
                                    onClick={() => toggleExpand(`ent-rateio-${i}-${idx}`)}
                                    style={{ marginLeft: 5 }}
                                  />
                                </div>
                                <div className="subclass-value-pct">
                                  <span className="subclass-value value-positive4">R$ {formatCurrency(ent.valor)}</span>
                                  <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                                </div>
                              </div>
                              {expanded.includes(`ent-rateio-${i}-${idx}`) && (
                                <div className="subclass-container">
                                  {ent.detalhes.map((det, dIdx) => (
                                    <div className="detail-row" key={`ent-rateio-det-${i}-${idx}-${dIdx}`}>
                                      <span className="detail-label">{det.mes}</span>
                                      <div className="subclass-value-pct">
                                        <span className="detail-value value-positive4">R$ {formatCurrency(det.valor)}</span>
                                        <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      )}

                      <div
                        className="row-between clickable"
                        onClick={() => toggleExpand(`co-${i}`)}
                        aria-expanded={expanded.includes(`co-${i}`)}
                        title="Ver detalhes de Despesas Operacionais"
                      >
                        <div className="dre-title">
                          Despesas Operacionais <span className="dre-note">(Despesas ligadas às placas)</span>
                          <img
                            style={{ marginLeft: 5 }}
                            src={expanded.includes(`co-${i}`) ? ArrowUp : ArrowDown}
                            alt="Toggle"
                            className="imgIcon arrow-toggle"
                          />
                        </div>
                        {renderValorPct(
                          <span className="entry-text value-negative3">R$ -{formatCurrency(despesasOperacionais)}</span>,
                          -Math.abs(despesasOperacionais),
                          baseRbAbs,
                          'value-negative5'
                        )}
                      </div>
                      {expanded.includes(`co-${i}`) && (
                        <div className="subclass-container">
                          {saidasDir.map((sai, idx) => (
                            <React.Fragment key={`sai-dir-${i}-${idx}`}>
                              <div className="subclass-row">
                                <div className="subclass-label">
                                  <span>{sai.classificacao}</span>
                                  <img
                                    src={expanded.includes(`sai-dir-${i}-${idx}`) ? ArrowUp : ArrowDown}
                                    alt="Toggle"
                                    className="imgIcon arrow-toggle clickable"
                                    onClick={() => toggleExpand(`sai-dir-${i}-${idx}`)}
                                    style={{ marginLeft: 5 }}
                                  />
                                </div>
                                <div className="subclass-value-pct">
                                  <span className="subclass-value value-negative4">R$ {formatCurrency(sai.valor)}</span>
                                  <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                                </div>
                              </div>
                              {expanded.includes(`sai-dir-${i}-${idx}`) && (
                                <div className="subclass-container">
                                  {sai.detalhes.map((det, dIdx) => (
                                    <div className="detail-row" key={`sai-dir-det-${i}-${idx}-${dIdx}`}>
                                      <span className="detail-label">{det.mes}</span>
                                      <div className="subclass-value-pct">
                                        <span className="detail-value value-negative4">R$ {formatCurrency(det.valor)}</span>
                                        <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      )}

                      <div className="row-between">
                        <div className="dre-title">
                          Resultado Operacional <span className="dre-note">(Receita Bruta - Despesa Operacional)</span>
                        </div>
                        {renderValorPct(
                          <span className={`entry-text ${classBySign3(resultadoOperacional)}`}>
                            R$ {formatCurrency(resultadoOperacional)}
                          </span>,
                          Number(resultadoOperacional) || 0,
                          baseRbAbs,
                          (Number(resultadoOperacional) || 0) < 0 ? 'value-negative5' : 'value-positive5'
                        )}
                      </div>

                      <div
                        className="row-between clickable"
                        onClick={() => toggleExpand(`mc-${i}`)}
                        aria-expanded={expanded.includes(`mc-${i}`)}
                        title="Ver detalhes da Margem de Contribuição"
                      >
                        <div className="dre-title">
                          Margem de Contribuição <span className="dre-note">(Rateio de despesas não ligadas às placas)</span>
                          <img
                            style={{ marginLeft: 5 }}
                            src={expanded.includes(`mc-${i}`) ? ArrowUp : ArrowDown}
                            alt="Toggle"
                            className="imgIcon arrow-toggle"
                          />
                        </div>
                        {renderValorPct(
                          <span className="entry-text value-negative3">R$ -{formatCurrency(margemContribuicao)}</span>,
                          -Math.abs(margemContribuicao),
                          baseRbAbs,
                          'value-negative5'
                        )}
                      </div>
                      {expanded.includes(`mc-${i}`) && (
                        <div className="subclass-container">
                          {saidasRate.map((sai, idx) => (
                            <React.Fragment key={`sai-rateio-${i}-${idx}`}>
                              <div className="subclass-row">
                                <div className="subclass-label">
                                  <span>{sai.classificacao}</span>
                                  <img
                                    src={expanded.includes(`sai-rateio-${i}_${idx}`) ? ArrowUp : ArrowDown}
                                    alt="Toggle"
                                    className="imgIcon arrow-toggle clickable"
                                    onClick={() => toggleExpand(`sai-rateio-${i}_${idx}`)}
                                    style={{ marginLeft: 5 }}
                                  />
                                </div>
                                <div className="subclass-value-pct">
                                  <span className="subclass-value value-negative4">R$ {formatCurrency(sai.valor)}</span>
                                  <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                                </div>
                              </div>
                              {expanded.includes(`sai-rateio-${i}_${idx}`) && (
                                <div className="subclass-container">
                                  {sai.detalhes.map((det, dIdx) => (
                                    <div className="detail-row" key={`sai-rateio-det-${i}-${idx}-${dIdx}`}>
                                      <span className="detail-label">{det.mes}</span>
                                      <div className="subclass-value-pct">
                                        <span className="detail-value value-negative4">R$ {formatCurrency(det.valor)}</span>
                                        <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      )}

                      <div className="row-between">
                        <div className="dre-title">
                          Resultado <span className="dre-note">(Resultado Operacional - Margem de Contribuição)</span>
                        </div>
                        {renderValorPct(
                          <span className={`entry-text ${classBySign3(resultadoFinal)}`}>R$ {formatCurrency(resultadoFinal)}</span>,
                          Number(resultadoFinal) || 0,
                          baseRbAbs,
                          (Number(resultadoFinal) || 0) < 0 ? 'value-negative5' : 'value-positive5'
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="modal-footer">
            <button className="modal-close-btn" onClick={onClose}>
              Fechar
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ModalCategoriaDre;
